import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Price } from '@thd-olt-component-react/price';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { CheckAvailability } from '@thd-olt-component-react/check-availability';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { Col } from '@thd-olt-component-react/grid';
import { FREE_SHIPPING_THRESHOLD } from './appliances/constants';
import { DeliveryMessage } from './appliances/DeliveryMessage';
import './Buybox.style.scss';

const Buybox = ({
  cartOptions,
  cartReqParams,
  finalPriceMessage,
  itemId,
  message,
  onAddToCartClick,
  pricing,
  strikethrough,
  applianceBundle,
  showCheckAvailability,
  checkedItems,
  storeId
}) => {

  const { channel } = useContext(ExperienceContext);
  const shouldShowFinalPriceMessage = finalPriceMessage && !applianceBundle;
  const shouldShowStrikethrough = strikethrough && !applianceBundle;
  const [checkAvailabilityDisableATC, setDisable] = useState(false);
  const [availabilityResponse, setAvailabilityResponse] = useState(null);

  const isApplianceDeliveryFree = (totalPrice) => {
    return totalPrice.pricing?.value >= FREE_SHIPPING_THRESHOLD;
  };

  const attachAvailabilityRefetch = (callbackData) => {
    const { hasOutOfStock, isUnavailable } = callbackData.processedData;
    setAvailabilityResponse(callbackData);
    if (hasOutOfStock || isUnavailable) {
      setDisable(true);
    } else {
      setDisable(false);
    }
  };

  useEffect(() => {
    if (showCheckAvailability && availabilityResponse && availabilityResponse.refetch) {
      const zip = window.cookieUtils?.readBrowserCookie?.('THD_AOL');
      availabilityResponse.refetch({ itemIds: checkedItems, zip });
    }
  }, [checkedItems, showCheckAvailability]);

  return (
    <>
      <div className="fbt-buybox__message-price">
        {message
        && (
          <div className="fbt-buybox__message">
            {message.priceMessage}
          </div>
        )}
        <LoadingPlaceholder
          type="text"
          ready={checkedItems.length > 1}
          color="#E0E0E0"
          rows={3}
          style={{ height: 100 }}
        >
          <div className="fbt-buybox__price">
            {shouldShowStrikethrough && <span className="fbt-buybox__strikethrough" />}
            <Price
              basic={applianceBundle}
              channel={channel}
              itemId={itemId}
              large={applianceBundle}
              product={pricing}
              displayEachUom={!applianceBundle}
              storeId={storeId}
            />
            {applianceBundle && <DeliveryMessage freeDelivery={isApplianceDeliveryFree(pricing)} />}
          </div>
        </LoadingPlaceholder>
      </div>
      {shouldShowFinalPriceMessage && (
        <div className="fbt-buybox__final-price-message">See Final Price in Cart</div>
      )}
      {showCheckAvailability && (
        <Col nopadding className="fbt-buybox__check-availability">
          <CheckAvailability
            itemIds={checkedItems}
            onSubmitForm={attachAvailabilityRefetch}
            hideInput
            autoCheck
            isBundle
            isMajorAppliance
          />
        </Col>
      )}
      <Col nopadding>
        <AddToCart
          cartOptions={cartOptions}
          cartReqParams={cartReqParams}
          onClick={onAddToCartClick}
          disabled={applianceBundle && checkAvailabilityDisableATC}
          outline
          small={false}
        >
          Add Items to Cart
        </AddToCart>
      </Col>
    </>
  );
};

Buybox.propTypes = {
  applianceBundle: PropTypes.bool,
  cartOptions: PropTypes.objectOf(PropTypes.any).isRequired,
  cartReqParams: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({})
  ]).isRequired,
  finalPriceMessage: PropTypes.bool,
  itemId: PropTypes.string.isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  onAddToCartClick: PropTypes.func.isRequired,
  pricing: PropTypes.shape({}).isRequired,
  strikethrough: PropTypes.bool,
  showCheckAvailability: PropTypes.bool,
  checkedItems: PropTypes.arrayOf(PropTypes.string),
  storeId: PropTypes.string.isRequired
};

Buybox.defaultProps = {
  applianceBundle: false,
  finalPriceMessage: false,
  strikethrough: false,
  showCheckAvailability: false,
  checkedItems: [],
};

Buybox.displayName = 'FBTBuybox';

export default Buybox;
